// pp-calc-tarima.js — ¿Cuántas Cajas Caben en una Tarima?
// (/pages/cuantas-cajas-caben-en-una-tarima)
//
// Paletizado sin sorpresas: metes las medidas de tu caja y la tarima, y te
// decimos cuántas caben por cama, cuántas camas y el total — con el dibujo
// del acomodo, que es lo que ninguna calculadora en español enseña.
//
// El acomodo NO es bin-packing general (eso es NP y aquí sería sobre-
// ingeniería): probamos los acomodos que un almacenista real usa —
// todas paradas igual, todas giradas, y los híbridos de "columnas + franja
// girada" en ambos ejes — y nos quedamos con el mejor. Para cajas
// rectangulares sobre tarima rectangular esto da el óptimo práctico.
//
// La aritmética vive aquí (no en la sección) para probarse con node a secas
// en tests/calculadoras-logica.test.mjs.
(function (root) {
  'use strict';

  // Un candidato de acomodo: lista de rects {x, y, w, h} en cm sobre la
  // tarima (origen esquina superior izquierda, x a lo largo de L).
  function rejilla(desdeX, desdeY, hastaX, hastaY, cajaW, cajaH) {
    var out = [];
    var nx = Math.floor((hastaX - desdeX) / cajaW);
    var ny = Math.floor((hastaY - desdeY) / cajaH);
    for (var i = 0; i < nx; i++) {
      for (var j = 0; j < ny; j++) {
        out.push({ x: desdeX + i * cajaW, y: desdeY + j * cajaH, w: cajaW, h: cajaH });
      }
    }
    return out;
  }

  // Bandas mixtas por programación dinámica: parte la dimensión "alto" del
  // plano (W) en bandas, cada banda completa de una sola orientación, y
  // elige la secuencia de bandas que mete más cajas. Esto encuentra los
  // acomodos clásicos que la rejilla uniforme pierde — p.ej. 40×30 en
  // 120×100: banda de 3 + banda de 3 + banda girada de 4 = 10 cajas justas.
  function bandas(tarimaL, tarimaW, cajaL, cajaW) {
    var H = Math.floor(tarimaW);
    var opciones = [
      { alto: cajaW, porBanda: Math.floor(tarimaL / cajaL), w: cajaL, h: cajaW },
      { alto: cajaL, porBanda: Math.floor(tarimaL / cajaW), w: cajaW, h: cajaL }
    ];
    // dp por cm de alto disponible; eleccion[y] recuerda la banda usada.
    var dp = new Array(H + 1).fill(0);
    var eleccion = new Array(H + 1).fill(null);
    for (var y = 1; y <= H; y++) {
      dp[y] = dp[y - 1];
      for (var o = 0; o < opciones.length; o++) {
        var op = opciones[o];
        var alto = Math.ceil(op.alto);
        if (op.porBanda <= 0 || alto > y) continue;
        var cand = dp[y - alto] + op.porBanda;
        if (cand > dp[y]) { dp[y] = cand; eleccion[y] = op; }
      }
    }
    // Reconstrucción de las bandas para poder dibujarlas. eleccion[y] es la
    // banda que TERMINA en y; donde es null, ese cm quedó sin banda.
    var out = [];
    var yCm = H;
    var yReal = 0;
    var pila = [];
    while (yCm > 0) {
      var el = eleccion[yCm];
      if (!el) { yCm--; continue; }
      pila.push(el);
      yCm -= Math.ceil(el.alto);
    }
    for (var p = pila.length - 1; p >= 0; p--) {
      var band = pila[p];
      for (var i = 0; i < band.porBanda; i++) {
        out.push({ x: i * band.w, y: yReal, w: band.w, h: band.h });
      }
      yReal += band.alto;
    }
    return out;
  }

  // Mejor acomodo de cajas (huella l×w, cm) sobre tarima L×W (cm).
  // Devuelve { total, cajas: [rects] }. Sin overhang: nada sale de la tarima.
  function mejorAcomodo(tarimaL, tarimaW, cajaL, cajaW) {
    if (!(tarimaL > 0 && tarimaW > 0 && cajaL > 0 && cajaW > 0)) return { total: 0, cajas: [] };

    var candidatos = [];

    // A: todas con l a lo largo. B: todas giradas.
    candidatos.push(rejilla(0, 0, tarimaL, tarimaW, cajaL, cajaW));
    candidatos.push(rejilla(0, 0, tarimaL, tarimaW, cajaW, cajaL));

    // Bandas mixtas en ambos ejes (la transpuesta se des-transpone para
    // que las coordenadas queden en el plano real de la tarima).
    candidatos.push(bandas(tarimaL, tarimaW, cajaL, cajaW));
    candidatos.push(bandas(tarimaW, tarimaL, cajaL, cajaW).map(function (b) {
      return { x: b.y, y: b.x, w: b.h, h: b.w };
    }));

    // Híbridos: bloque principal + franja restante con cajas giradas.
    // Se prueba recortando la franja en cada eje y con ambas orientaciones base.
    var oris = [[cajaL, cajaW], [cajaW, cajaL]];
    for (var k = 0; k < oris.length; k++) {
      var bl = oris[k][0], bw = oris[k][1];
      var nx = Math.floor(tarimaL / bl);
      var ny = Math.floor(tarimaW / bw);
      // franja vertical sobrante (a lo largo de W completa)
      var principalV = rejilla(0, 0, nx * bl, tarimaW, bl, bw);
      var franjaV = rejilla(nx * bl, 0, tarimaL, tarimaW, bw, bl);
      candidatos.push(principalV.concat(franjaV));
      // franja horizontal sobrante (a lo largo de L completa)
      var principalH = rejilla(0, 0, tarimaL, ny * bw, bl, bw);
      var franjaH = rejilla(0, ny * bw, tarimaL, tarimaW, bw, bl);
      candidatos.push(principalH.concat(franjaH));
    }

    var mejor = candidatos[0];
    for (var c = 1; c < candidatos.length; c++) {
      if (candidatos[c].length > mejor.length) mejor = candidatos[c];
    }
    return { total: mejor.length, cajas: mejor };
  }

  // Cálculo completo de la tarima. Todo en cm y kg.
  //   cajaL, cajaW, cajaH — medidas EXTERIORES de la caja
  //   tarimaL, tarimaW    — huella de la tarima
  //   alturaMax           — altura total permitida (tarima incluida)
  //   alturaTarima        — alto de la madera (default 15 cm)
  //   pesoCaja (opcional) — kg por caja llena
  function calcularTarima(e) {
    if (!e) return null;
    var cl = n(e.cajaL), cw = n(e.cajaW), ch = n(e.cajaH);
    var tl = n(e.tarimaL), tw = n(e.tarimaW);
    var hMax = n(e.alturaMax);
    var hTarima = n(e.alturaTarima); if (hTarima === null) hTarima = 15;
    if ([cl, cw, ch, tl, tw, hMax].some(function (v) { return v === null || v <= 0; })) return null;

    var acomodo = mejorAcomodo(tl, tw, cl, cw);
    if (acomodo.total === 0) return { error: 'caja_no_cabe' };

    var alturaUtil = hMax - hTarima;
    var camas = Math.floor(alturaUtil / ch);
    if (camas <= 0) return { error: 'altura_insuficiente' };

    var total = acomodo.total * camas;
    var alturaCarga = camas * ch;
    var r = {
      porCama: acomodo.total,
      camas: camas,
      total: total,
      alturaCarga: alturaCarga,
      alturaTotal: alturaCarga + hTarima,
      layout: acomodo.cajas,
      tarimaL: tl,
      tarimaW: tw,
      cajaL: cl,
      cajaW: cw,
      cajaH: ch,
      advertencias: []
    };

    var pesoCaja = n(e.pesoCaja);
    if (pesoCaja !== null && pesoCaja > 0) {
      r.pesoTotal = total * pesoCaja;
      // Una tarima estándar de madera trabaja cómoda hasta ~1,200–1,500 kg.
      if (r.pesoTotal > 1200) r.advertencias.push('peso');
    }
    // Aprovechamiento de la huella (para presumir qué tan bueno es el acomodo)
    r.aprovechamientoPct = (acomodo.total * cl * cw) / (tl * tw) * 100;
    return r;
  }

  // Metros de emplaye para envolver la tarima. Regla práctica:
  // pasadas en espiral con traslape del 50% (subir y bajar) + 4 vueltas de
  // arranque/remate en la base. Película aplicada a mano, sin pre-estiro.
  function emplayeNecesario(e) {
    var tl = n(e.tarimaL), tw = n(e.tarimaW), h = n(e.alturaCarga);
    var anchoFilm = n(e.anchoFilmCm); if (anchoFilm === null) anchoFilm = 45;
    if ([tl, tw, h].some(function (v) { return v === null || v <= 0; }) || anchoFilm <= 0) return null;
    var perimetroM = 2 * (tl + tw) / 100;
    var avance = anchoFilm * 0.5; // 50% de traslape entre vueltas
    var vueltasEspiral = Math.ceil(h / avance) * 2; // subir y bajar
    var vueltas = vueltasEspiral + 4;
    return { metros: perimetroM * vueltas, vueltas: vueltas };
  }

  // Metros de fleje: el patrón estándar de 4 flejes verticales (2 por eje).
  // Cada fleje rodea alto + lado y de regreso, + 10% para tensar y sellar.
  function flejeNecesario(e) {
    var tl = n(e.tarimaL), tw = n(e.tarimaW), h = n(e.alturaTotal);
    if ([tl, tw, h].some(function (v) { return v === null || v <= 0; })) return null;
    var porFlejeL = 2 * (h + tw) / 100; // cruza a lo ancho
    var porFlejeW = 2 * (h + tl) / 100; // cruza a lo largo
    var metros = (2 * porFlejeL + 2 * porFlejeW) * 1.1;
    return { metros: metros, flejes: 4, hebillas: 4 };
  }

  function n(x) {
    if (x === null || x === undefined || x === '') return null;
    var v = Number(String(x).replace(',', '.'));
    return isFinite(v) ? v : null;
  }

  root.PPCalcTarima = {
    mejorAcomodo: mejorAcomodo,
    calcularTarima: calcularTarima,
    emplayeNecesario: emplayeNecesario,
    flejeNecesario: flejeNecesario
  };
})(typeof window !== 'undefined' ? window : globalThis);

// ── Wiring de la página (no corre en tests de node) ────────────────────────
(function () {
  'use strict';
  if (typeof document === 'undefined') return;

  function listo(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  listo(function () {
    var raiz = document.querySelector('[data-pp-calc-tarima]');
    if (!raiz) return;
    var C = window.PPCalcTarima;
    var $ = function (sel) { return raiz.querySelector(sel); };
    var form = $('form');
    var out = $('[data-calc-resultado]');

    // La tarima custom muestra sus dos inputs solo al elegir "otra".
    // Telemetría anónima de uso (fire-and-forget): qué cargas calcula la
    // gente nos dice qué cajas/tarimas demandan. Sin PII; si falla, silencio.
    function telemetria(inputs, resultado) {
      try {
        var KEY = 'sb_publishable_48y6S7h2eQ-9uTF7pCNJNQ_Q0mz8VVX';
        fetch('https://dhygbnysealeblqorjts.supabase.co/functions/v1/calc-telemetria', {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', 'apikey': KEY, 'Authorization': 'Bearer ' + KEY },
          body: JSON.stringify({ calculadora: 'tarima', inputs: inputs, resultado: resultado })
        }).catch(function () {});
      } catch (_) { /* la telemetría jamás rompe la calculadora */ }
    }

    var selTarima = $('[name="tarima"]');
    var customWrap = $('[data-tarima-custom]');
    if (selTarima && customWrap) {
      selTarima.addEventListener('change', function () {
        customWrap.hidden = selTarima.value !== 'custom';
      });
    }

    // conTelemetria: solo el submit explícito reporta; el recálculo en vivo
    // al editar campos no, para no llenar la tabla con teclazos intermedios.
    function correr(conTelemetria) {
      var f = new FormData(form);
      var dims = dimsTarima(f);
      var r = C.calcularTarima({
        cajaL: f.get('caja_l'), cajaW: f.get('caja_w'), cajaH: f.get('caja_h'),
        pesoCaja: f.get('peso_caja'),
        tarimaL: dims.l, tarimaW: dims.w,
        alturaMax: f.get('altura_max'), alturaTarima: 15
      });
      pintar(r);
      if (conTelemetria && r && !r.error) {
        telemetria(
          { caja_l: r.cajaL, caja_w: r.cajaW, caja_h: r.cajaH, tarima_l: r.tarimaL, tarima_w: r.tarimaW, altura_max: Number(f.get('altura_max')) || null, peso_caja: Number(f.get('peso_caja')) || null },
          { por_cama: r.porCama, camas: r.camas, total: r.total, aprovechamiento_pct: Math.round(r.aprovechamientoPct * 10) / 10, peso_total: r.pesoTotal || null }
        );
      }
    }

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      correr(true);
      if (out) out.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    // Con el resultado ya visible, editar cualquier campo recalcula solo —
    // Padrino escribió el peso DESPUÉS de calcular y el total no aparecía.
    var timerRecalc = null;
    function recalcEnVivo() {
      if (!out || out.hidden) return;
      clearTimeout(timerRecalc);
      timerRecalc = setTimeout(function () { correr(false); }, 350);
    }
    form.addEventListener('input', recalcEnVivo);
    form.addEventListener('change', recalcEnVivo);

    function dimsTarima(f) {
      var v = f.get('tarima');
      if (v === 'custom') return { l: Number(f.get('tarima_l')), w: Number(f.get('tarima_w')) };
      var partes = String(v).split('x'); // "120x100"
      return { l: Number(partes[0]), w: Number(partes[1]) };
    }

    function fmt(x, dec) {
      return x.toLocaleString('es-MX', { maximumFractionDigits: dec === undefined ? 0 : dec });
    }

    function pintar(r) {
      if (!out) return;
      out.hidden = false;
      if (!r || r.error) {
        // Sin resultado válido no debe quedar un dibujo viejo colgado
        var contErr = raiz.querySelector('[data-calc-dibujo-3d]');
        if (contErr) contErr.hidden = true;
        var pieErr = raiz.querySelector('[data-calc-dibujo-pie]');
        if (pieErr) pieErr.hidden = true;
      }
      if (!r) {
        out.innerHTML = '<p class="pp-calc__error">Revisa los datos: medidas de caja, tarima y altura máxima deben ser mayores a cero.</p>';
        return;
      }
      if (r.error === 'caja_no_cabe') {
        out.innerHTML = '<p class="pp-calc__error">La caja es más grande que la tarima — no cabe ni una sin volarse de la orilla.</p>';
        return;
      }
      if (r.error === 'altura_insuficiente') {
        out.innerHTML = '<p class="pp-calc__error">Con esa altura máxima no cabe ni una cama de cajas encima de la tarima (15 cm de madera). Sube la altura o revisa el alto de la caja.</p>';
        return;
      }

      var html = '<div class="pp-calc__veredicto pp-calc__veredicto--ok">';
      html += '<p class="pp-calc__veredicto-titulo">Caben ' + fmt(r.total) + ' cajas en la tarima</p>';
      html += '<p class="pp-calc__veredicto-dato"><strong>' + fmt(r.porCama) + ' cajas por cama × ' + fmt(r.camas) + ' camas</strong> · estiba de ' + fmt(r.alturaTotal) + ' cm de alto (madera incluida).</p>';
      html += '</div>';

      html += '<dl class="pp-calc__detalle">';
      html += par('Aprovechamiento de la huella de la tarima', fmt(r.aprovechamientoPct, 1) + '%');
      if (r.pesoTotal) html += par('Peso total estimado de la carga', fmt(r.pesoTotal) + ' kg');
      // Gancho de compra directa (2-sep): el resultado ya dice cuánto
      // material necesitas — que el link para comprarlo esté ahí mismo.
      var urlEmplaye = raiz.dataset.urlEmplaye || '';
      var urlFleje = raiz.dataset.urlFleje || '';
      var em = C.emplayeNecesario({ tarimaL: r.tarimaL, tarimaW: r.tarimaW, alturaCarga: r.alturaCarga, anchoFilmCm: 45 });
      if (em) {
        var rollos = Math.ceil(em.metros / 243); // rollo manual típico de 800 ft
        var vEm = '≈ ' + fmt(em.metros) + ' m (' + rollos + (rollos === 1 ? ' rollo' : ' rollos') + ' de 18")';
        if (urlEmplaye) vEm += ' <a class="pp-calc__mini-cta" href="' + urlEmplaye + '">Comprar Emplaye</a>';
        html += par('Emplaye para envolverla (film manual de 18", cal 80 ≈ 20 micras, traslape 50%)', vEm);
      }
      var fl = C.flejeNecesario({ tarimaL: r.tarimaL, tarimaW: r.tarimaW, alturaTotal: r.alturaTotal });
      if (fl) {
        var vFl = '≈ ' + fmt(fl.metros) + ' m + 4 hebillas';
        if (urlFleje) vFl += ' <a class="pp-calc__mini-cta" href="' + urlFleje + '">Comprar Fleje</a>';
        html += par('Fleje con patrón de 4 amarres', vFl);
      }
      html += '</dl>';

      if (r.advertencias.indexOf('peso') !== -1) {
        html += '<p class="pp-calc__nota">Ojo: la carga pasa de 1,200 kg — una tarima de madera estándar anda al límite. Considera repartir en dos tarimas o usar una reforzada.</p>';
      }
      out.innerHTML = html;
      dibujar(r);

      function par(t, v) { return '<div class="pp-calc__detalle-fila"><dt>' + t + '</dt><dd>' + v + '</dd></div>'; }
    }

    // La estiba COMPLETA en 3D (pedido de Padrino, 1-sep): la tarima de
    // madera con todas las camas de cajas encima, girable. Usa el motor
    // compartido pp-caja-3d con mostrarCaja:false (aquí no hay caja
    // contenedora: el volumen de referencia es la estiba entera).
    function dibujar(r) {
      var cont = raiz.querySelector('[data-calc-dibujo-3d]');
      var pie = raiz.querySelector('[data-calc-dibujo-pie]');
      if (!cont || !window.PPCaja3D || !window.PPCaja3D.montar) return;
      cont.hidden = false;

      var piezas = [];
      // La madera (color 5 de la paleta): cubierta + 3 travesaños
      piezas.push({ dims: [r.tarimaL, r.tarimaW, 3], pos: [0, 12, 0], color: 5 });
      [0, (r.tarimaL - 10) / 2, r.tarimaL - 10].forEach(function (x) {
        piezas.push({ dims: [10, r.tarimaW, 12], pos: [x, 0, 0], color: 5 });
      });

      // Las cajas, cama por cama (tono alternado para leer los niveles).
      // Tope de dibujo para no ahogar el canvas en estibas enormes.
      var TOPE = 260;
      var camasDibujadas = 0;
      for (var cama = 0; cama < r.camas; cama++) {
        if (piezas.length + r.layout.length > TOPE + 4) break;
        for (var i = 0; i < r.layout.length; i++) {
          var b = r.layout[i];
          piezas.push({
            dims: [b.w, b.h, r.cajaH],
            pos: [b.x, 15 + cama * r.cajaH, b.y],
            color: cama % 2
          });
        }
        camasDibujadas++;
      }

      window.PPCaja3D.montar(cont, {
        caja: [r.tarimaL, r.tarimaW, r.alturaTotal],
        mostrarCaja: false,
        piezas: piezas,
        etiqueta: 'Tu estiba en 3D — arrástrala para girarla. Tarima de ' + fmt(r.tarimaL) + ' × ' + fmt(r.tarimaW) +
          ' cm con ' + fmt(r.porCama) + ' cajas de ' + fmt(r.cajaL) + ' × ' + fmt(r.cajaW) + ' × ' + fmt(r.cajaH) +
          ' cm por cama' + (camasDibujadas < r.camas ? ' (se dibujan ' + camasDibujadas + ' de ' + r.camas + ' camas)' : '')
      });

      if (pie) {
        pie.hidden = false;
        pie.textContent = 'Los dos tonos alternan las camas. El acomodo de cada cama es el mejor que encontramos entre rejillas, giros e híbridos.';
      }
    }
  });
})();
